import { useEffect, useState } from "react";
import axios from "axios";
import { Search, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TavilyUsageCard() {
  const [usage, setUsage] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/tavily-usage`).then((r) => setUsage(r.data)).catch(() => {});
  }, []);

  if (!usage) return null;

  const pct = Math.min(usage.percent_used, 100);
  const thresholdPct = (usage.alert_threshold / usage.monthly_limit) * 100;

  return (
    <div className={`border p-6 mb-12 ${usage.alert ? "bg-red-50 border-red-300" : "bg-white border-slate-200"}`} data-testid="tavily-usage-card">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-5">
        <div className="flex items-center gap-3">
          <Search className={`w-4 h-4 ${usage.alert ? "text-red-600" : "text-[#009EDB]"}`} aria-hidden="true" />
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Live Law Lookup — Tavily Search Credits · {usage.month}</p>
            {usage.paused && (
              <span className="inline-block text-[11px] font-bold uppercase tracking-[0.15em] text-white bg-red-600 px-2 py-0.5 mt-1" data-testid="tavily-paused-badge">
                Lookups Auto-Paused
              </span>
            )}
            <p className="font-mono text-2xl font-semibold text-slate-900 mt-1" data-testid="tavily-credits-used">
              {usage.credits_used}<span className="text-sm text-slate-400 font-normal"> / {usage.monthly_limit} used</span>
            </p>
          </div>
        </div>
        <div className="text-left md:text-right">
          <p className="font-mono text-sm text-slate-600" data-testid="tavily-credits-remaining">{usage.remaining} remaining</p>
          <p className="text-xs text-slate-400 mt-0.5">Alert threshold: {usage.alert_threshold} credits</p>
        </div>
      </div>
      <div className="relative h-3 bg-slate-100 border border-slate-200" data-testid="tavily-usage-bar">
        <div
          className={`absolute inset-y-0 left-0 ${usage.alert ? "bg-red-500" : pct >= thresholdPct * 0.75 ? "bg-amber-400" : "bg-[#0050A0]"}`}
          style={{ width: `${pct}%` }}
        />
        <div className="absolute inset-y-0 border-l-2 border-dashed border-red-400" style={{ left: `${thresholdPct}%` }} title={`Alert threshold: ${usage.alert_threshold}`} />
      </div>
      {usage.alert && (
        <div className="mt-4 flex items-start gap-2 text-sm text-red-700" data-testid="tavily-usage-alert">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />
          <p>
            {usage.paused ? (
              <>
                <strong>Auto-pause active:</strong> the {usage.monthly_limit}-credit monthly limit is reached — live web
                lookups are paused so the plan cannot overrun. Profiles still generate from model knowledge; lookups
                resume automatically on the 1st of next month.
              </>
            ) : (
              <>
                <strong>Credit alert:</strong> monthly usage has crossed the {usage.alert_threshold}-credit threshold
                ({usage.credits_used} of {usage.monthly_limit}). Live law lookups will auto-pause at the
                {" "}{usage.monthly_limit}-credit limit so the plan never overruns.
              </>
            )}
          </p>
        </div>
      )}
    </div>
  );
}
